import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 *
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  return cartData.map((productData) => {
    const productItem = productsData.find(
      (item) => item._id === productData.productId
    );
    const { name, cost, image } = productItem;
    const { qty } = productData;

    const cartItem = {
      productId: productData.productId,
      name,
      cost,
      qty,
      image,
    };
    return cartItem;
  });
};

const displayCartView = (cartItems, handleQuantity, isReadOnly) => {
  return (
    <Stack mx={1} my={1}>
      {cartItems.map((cartItem) => {
        return (
          <Box
            display="flex"
            alignItems="flex-start"
            padding="1rem"
            key={cartItem.productId}
          >
            <Box className="image-container">
              <img
                // Add product image
                src={cartItem.image}
                // Add product name as alt eext
                alt={cartItem.name}
                width="100%"
                height="100%"
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              height="6rem"
              paddingX="1rem"
            >
              <div>{cartItem.name}</div>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {isReadOnly ? (
                  CheckoutCartView(cartItem.qty)
                ) : (
                  <ItemQuantity
                    value={cartItem.qty}
                    handleAdd={() => handleQuantity(cartItem, true)}
                    handleDelete={() => handleQuantity(cartItem, false)}

                    // Add required props by checking implementation
                  />
                )}
                <Box padding="0.5rem" fontWeight="700">
                  ${cartItem.cost}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  if (!items.length) return 0;
  let sum = 0;
  for (let i = 0; i < items.length; i++) {
    sum += items[i].cost * items[i].qty;
    // sum += (items[i].totalCost);
  }
  return sum;
};

/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 *
 */
// const ItemQuantity = ({ value, handleAdd, handleDelete }) => {
// };

// TODO: CRIO_TASK_MODULE_CHECKOUT - Implement function to return total cart quantity
/**
 * Return the sum of quantities of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products in cart
 *
 * @returns { Number }
 *    Total quantity of products added to the cart
 *
 */
export const getTotalItems = (items = []) => {
  return items.reduce((total, item) => total + item.qty, 0);
};

// TODO: CRIO_TASK_MODULE_CHECKOUT - Add static quantity view for Checkout page cart
/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 *
 */
const CheckoutCartView = (value) => {
  return (
    <Stack direction="row" alignItems="center">
      <Box padding="0.5rem" data-testid="item-qty">
        Qty:{value}
      </Box>
    </Stack>
  );
};

const CheckoutOrderDetailsSection = (items) => {
  return (
    <Stack pt={2} pb={2.5}>
      <Box
        color="#3C3C3C"
        alignSelf="left"
        fontWeight="700"
        fontSize="1.2rem"
        paddingLeft="1rem"
      >
        Order Details
      </Box>
      <Box
        padding="1rem"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box color="#3C3C3C" alignSelf="center">
          Products
        </Box>
        <Box color="#3C3C3C" alignSelf="center">
          {getTotalItems(items)}
        </Box>
      </Box>

      <Box
        paddingX="1rem"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box color="#3C3C3C" alignSelf="center">
          Subtotal
        </Box>
        <Box color="#3C3C3C" alignSelf="center">
          ${getTotalCartValue(items)}
        </Box>
      </Box>

      <Box
        padding="1rem"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box color="#3C3C3C" alignSelf="center">
          Shipping Charges
        </Box>
        <Box color="#3C3C3C" alignSelf="center">
          $0
        </Box>
      </Box>

      <Box
        paddingX="1rem"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box
          color="#3C3C3C"
          fontWeight="700"
          fontSize="1.1rem"
          alignSelf="center"
        >
          Total
        </Box>
        <Box
          color="#3C3C3C"
          fontWeight="700"
          fontSize="1.1rem"
          alignSelf="center"
          data-testid="cart-total"
        >
          ${getTotalCartValue(items)}
        </Box>
      </Box>
    </Stack>
  );
};

const ItemQuantity = ({ value, handleAdd, handleDelete }) => {
  return (
    <Stack direction="row" alignItems="center">
      <IconButton size="small" color="primary" onClick={handleDelete}>
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton size="small" color="primary" onClick={handleAdd}>
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 *
 */

/*
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 *
 */
const Cart = ({ products, items = [], handleQuantity, isReadOnly = false }) => {
  const history = useHistory();

  useEffect(() => {}, []);

  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className="cart">
        {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}
        {displayCartView(items, handleQuantity, isReadOnly)}

        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>

        {!isReadOnly && (
          <Box display="flex" justifyContent="flex-end" className="cart-footer">
            <Button
              color="primary"
              variant="contained"
              startIcon={<ShoppingCart />}
              name="checkout"
              className="checkout-btn"
              onClick={() => history.push("/checkout")}
            >
              checkout
            </Button>
          </Box>
        )}
      </Box>
      <Box className="cart">
        {isReadOnly && CheckoutOrderDetailsSection(items)}
      </Box>
    </>
  );
};

export default Cart;
